"use client"

import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import dollar from "../../../assets/icons/dollar.svg"
import percentage from "../../../assets/icons/percentage.svg"
import Close from "../../../assets/svgs/Close.svg"
import InputWithIcon from "../../../components/InputWithIcon"
import SearchDropdown from "../../../components/Dropdowns/SearchDropdown"
import useAuth from "../../../hooks/useAuth"
import { BASE_URL } from "../../../utils/Element"

const AddCommissionData = ({
  showModal,
  formData,
  search,
  disable,
  error,
  onSetFormData,
  onSetError,
  handleSubmit,
  handleClose,
  isBuyerBDSDollar,
  setIsBuyerBDSDollar,
  isDollar,
  setIsDollar,
}) => {
  const [config] = useAuth()
  const { id } = useParams()
  const navigate = useNavigate()

  const [showConfirm, setShowConfirm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [brokerOptions, setBrokerOptions] = useState({})
  const [bdsOptions, setBdsOptions] = useState({})

  const fetchBrokerSuggestions = async (query, index) => {
    if (!query) {
      setBrokerOptions((prev) => ({ ...prev, [index]: [] }))
      return
    }
    try {
      const [resContacts, resUsers] = await Promise.all([
        axios.get(`${BASE_URL}/get-datalisting?list=contacts&search=${query}`, config).catch(() => null),
        axios.get(`${BASE_URL}/get-datalisting?list=users&search=${query}`, config).catch(() => null),
      ])

      const list = []

      const contacts = resContacts?.data?.data?.contact_list || resContacts?.data?.data || []
      if (Array.isArray(contacts)) {
        contacts.forEach((c) => {
          const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name
          if (name && c.id) {
            list.push({ value: c.id, label: name, desc: "Contact" })
          }
        })
      }

      const users = resUsers?.data?.data || []
      if (Array.isArray(users)) {
        users.forEach((u) => {
          const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name
          if (name && u.id && !list.some((item) => item.value === u.id)) {
            list.push({ value: u.id, label: name, desc: "User" })
          }
        })
      }

      setBrokerOptions((prev) => ({ ...prev, [index]: list }))
    } catch (err) {
      console.error(err)
    }
  }

  const fetchBdsSuggestions = async (query, index) => {
    if (!query) {
      setBdsOptions((prev) => ({ ...prev, [index]: [] }))
      return
    }
    try {
      const [resContacts, resUsers] = await Promise.all([
        axios.get(`${BASE_URL}/get-datalisting?list=contacts&search=${query}`, config).catch(() => null),
        axios.get(`${BASE_URL}/get-datalisting?list=users&search=${query}`, config).catch(() => null),
      ])

      const list = []

      const contacts = resContacts?.data?.data?.contact_list || resContacts?.data?.data || []
      if (Array.isArray(contacts)) {
        contacts.forEach((c) => {
          const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name
          if (name && c.id) {
            list.push({ value: c.id, label: name, desc: "Contact" })
          }
        })
      }

      const users = resUsers?.data?.data || []
      if (Array.isArray(users)) {
        users.forEach((u) => {
          const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name
          if (name && u.id && !list.some((item) => item.value === u.id)) {
            list.push({ value: u.id, label: name, desc: "User" })
          }
        })
      }

      setBdsOptions((prev) => ({ ...prev, [index]: list }))
    } catch (err) {
      console.error(err)
    }
  }

  /* -----------------------------------------------
     INIT
  -------------------------------------------------- */
  useEffect(() => {
    const init = {}

    if (!Array.isArray(formData.brokers)) init.brokers = []
    if (!Array.isArray(formData.bds)) init.bds = []

    if (typeof formData.gross_commission_income_manual !== "boolean")
      init.gross_commission_income_manual = false

    if (typeof formData.company_gross_commission_manual !== "boolean")
      init.company_gross_commission_manual = false

    const numericFields = [
      "total_commission",
      "broker_record_fee",
      "referal_fee",
      "misc_fee",
      "gross_commission_income",
      "company_gross_commission",
    ]

    numericFields.forEach((k) => {
      if (typeof formData[k] === "undefined") init[k] = ""
    })

    if (Object.keys(init).length > 0) {
      onSetFormData({ ...formData, ...init })
    }
  }, [])

  /* -----------------------------------------------
     HELPERS
  -------------------------------------------------- */
  const toNumber = (v) => {
    if (v === "" || v === null || v === undefined) return 0
    const n = Number(v)
    return isNaN(n) ? 0 : n
  }

  const getShareDollar = (items, amountKey, gross) =>
    (items || []).reduce((sum, item) => {
      const amount = toNumber(item?.[amountKey] || 0)
      const grossNum = toNumber(gross || 0)
      if (item?.type === "dollar") {
        return sum + amount
      } else if (item?.type === "percentage") {
        return sum + grossNum * amount / 100
      }
      return sum
    }, 0)

  const calculateGrossCommission = () => {
    return Math.max(
      toNumber(formData.total_commission) -
      (toNumber(formData.broker_record_fee) +
        toNumber(formData.referal_fee) +
        toNumber(formData.misc_fee)),
      0
    )
  }

  const calculateBrokerShare = () =>
    getShareDollar(formData.brokers, "amount_broker", formData.gross_commission_income)

  const calculateBdsShare = () =>
    getShareDollar(formData.bds, "amount", formData.gross_commission_income)

  const calculateCompanyGross = () =>
    calculateGrossCommission() -
    (calculateBrokerShare() + calculateBdsShare())

  /* -----------------------------------------------
     AUTO CALC
  -------------------------------------------------- */
  useEffect(() => {
    const payload = {}

    const grossFinal =
      formData.gross_commission_income_manual &&
        formData.gross_commission_income !== ""
        ? toNumber(formData.gross_commission_income)
        : calculateGrossCommission()

    const companyFinal =
      formData.company_gross_commission_manual &&
        formData.company_gross_commission !== ""
        ? toNumber(formData.company_gross_commission)
        : calculateCompanyGross()

    if (toNumber(formData.gross_commission_income) !== grossFinal)
      payload.gross_commission_income = grossFinal

    if (toNumber(formData.company_gross_commission) !== companyFinal)
      payload.company_gross_commission = companyFinal

    if (Object.keys(payload).length)
      onSetFormData({ ...formData, ...payload })
  }, [formData]) // Removed unused deps; formData changes trigger recalc including types

  /* -----------------------------------------------
     UPDATE HANDLERS
  -------------------------------------------------- */
  const updateBroker = (index, field, value) => {
    const brokers = [...(formData.brokers || [])]
    brokers[index] = { ...brokers[index], [field]: value }
    onSetFormData({ ...formData, brokers })
  }

  const addBroker = () => {
    const brokers = [...(formData.brokers || [])]
    brokers.push({
      contact: { id: "", name: "" },
      commission_amount_broker: "",
      amount_broker: "",
      type: "dollar"
    })
    onSetFormData({ ...formData, brokers })
  }

  const removeBroker = (index) => {
    const brokers = [...(formData.brokers || [])]
    brokers.splice(index, 1)
    onSetFormData({ ...formData, brokers })
  }

  const updateBDS = (index, field, value) => {
    const bds = [...(formData.bds || [])]
    bds[index] = { ...bds[index], [field]: value }
    onSetFormData({ ...formData, bds })
  }

  const addBDS = () => {
    const bds = [...(formData.bds || [])]
    bds.push({
      contact: { id: "", name: "" },
      commission_amount_bds: "",
      amount: "",
      type: "dollar"
    })
    onSetFormData({ ...formData, bds })
  }

  const removeBDS = (index) => {
    const bds = [...(formData.bds || [])]
    bds.splice(index, 1)
    onSetFormData({ ...formData, bds })
  }

  const handleNumericFieldChange = (key, e, manualKey) => {
    const value = e.target.value.replace(/,/g, "")
    const payload = { [key]: value === "" ? "" : Number(value) }
    if (manualKey) payload[manualKey] = value !== ""
    onSetFormData({ ...formData, ...payload })
  }

  /* -----------------------------------------------
     SUBMIT FLOW (FIXED)
  -------------------------------------------------- */
  const handleFormSubmit = async (e) => {
    e.preventDefault()
    onSetError(null)
    setIsSubmitting(true)

    const submitError = await handleSubmit(false)
    setIsSubmitting(false)

    if (submitError) {
      onSetError(submitError)
      setShowConfirm(false)
      return
    }
    setShowConfirm(true)
  }

  const handleConfirmYes = async () => {
    onSetError(null)
    setIsSubmitting(true)

    const submitError = await handleSubmit(true)
    setIsSubmitting(false)

    if (submitError) {
      onSetError(submitError)
      return
    }

    setShowConfirm(false)
    handleClose()
  }

  const handleConfirmNo = () => setShowConfirm(false)

  /* -----------------------------------------------
     UI
  -------------------------------------------------- */
  return (
    <>
      <form onSubmit={handleFormSubmit}>
        {/* TOTAL COMMISSION */}
        <div className="mt-6">
          <label className="dark-H head-4">Total Commission</label>
          <InputWithIcon
            icon={dollar}
            placeholder="Total Commission"
            type="number"
            className="input-field body-N"
            value={formData.total_commission ?? ""}
            onChange={(e) => handleNumericFieldChange("total_commission", e)}
          />
        </div>

        {/* BROKER OF RECORD */}
        <div className="mt-6">
          <label className="dark-H head-4">Broker of Record Fee</label>
          <InputWithIcon
            icon={dollar}
            placeholder="Broker of Record Fee"
            type="number"
            className="input-field body-N"
            value={formData.broker_record_fee ?? ""}
            onChange={(e) => handleNumericFieldChange("broker_record_fee", e)}
          />
        </div>

        {/* REFERRAL FEE */}
        <div className="mt-6">
          <label className="dark-H head-4">Referral Fee</label>
          <InputWithIcon
            icon={dollar}
            placeholder="Referral Fee"
            type="number"
            className="input-field body-N"
            value={formData.referal_fee ?? ""}
            onChange={(e) => handleNumericFieldChange("referal_fee", e)}
          />
        </div>

        {/* MISC FEE */}
        <div className="mt-6">
          <label className="dark-H head-4">Misc. Fee</label>
          <InputWithIcon
            icon={dollar}
            type="number"
            className="input-field body-N"
            placeholder="Misc. Fee"
            value={formData.misc_fee ?? ""}
            onChange={(e) => handleNumericFieldChange("misc_fee", e)}
          />
        </div>

        {/* GROSS COMMISSION INCOME */}
        <div className="mt-6">
          <label className="dark-H head-4">Gross Commission Income</label>
          <InputWithIcon
            icon={dollar}
            type="number"
            className="input-field body-N"
            placeholder="Gross Commission Income"
            value={formData.gross_commission_income ?? ""}
            onChange={(e) => handleNumericFieldChange("gross_commission_income", e, "gross_commission_income_manual")}
          />
        </div>

        {/* PAY TO BROKERS */}
        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="head-4">Pay to Brokers</h3>
            <button
              type="button"
              onClick={addBroker}
              className="text-sm text-blue-600 font-semibold hover:underline"
            >
              + Add Broker
            </button>
          </div>

          {(formData.brokers?.length ? formData.brokers : [{}]).map((item, index) => {
            const isDollar = item.type === "dollar"

            return (
              <div key={index} className="mb-4 p-3 rounded-lg shadow-sm bg-white border border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Broker Name</label>
                  {(formData.brokers?.length || 0) > 1 && (
                    <button
                      type="button"
                      onClick={() => removeBroker(index)}
                      className="text-xs text-red-600 hover:underline font-semibold"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="mb-3">
                  <SearchDropdown
                    placeholder="Search broker..."
                    text={item?.contact?.name || ""}
                    options={brokerOptions[index] || []}
                    fetchSuggestions={(query) => fetchBrokerSuggestions(query, index)}
                    onSetOptions={(opts) => setBrokerOptions((prev) => ({ ...prev, [index]: opts }))}
                    handleKeyDown={(e) => {
                      if (e.key === "Backspace") {
                        updateBroker(index, "contact", { id: "", name: "" })
                      }
                    }}
                    handleSelect={(option) => {
                      updateBroker(index, "contact", { id: option.value, name: option.label })
                      setBrokerOptions((prev) => ({ ...prev, [index]: [] }))
                    }}
                  />
                </div>

                <div className="flex gap-4 items-center">
                  {/* Contribution % */}
                  <div className="flex-1">
                    <label className="block mb-1 dark-H head-4">
                      Contribution %
                    </label>

                    <InputWithIcon
                      icon={percentage}
                      placeholder="Contribution %"
                      type="number"
                      className="input-field body-N"
                      value={item.commission_amount_broker ?? ""}
                      onChange={(e) =>
                        updateBroker(
                          index,
                          "commission_amount_broker",
                          e.target.value
                        )
                      }
                    />
                  </div>

                  {/* Commission */}
                  <div className="flex-1">
                    <label className="block mb-1 dark-H head-4">
                      Commission
                    </label>

                    <div className="flex items-center gap-2">
                      <InputWithIcon
                        icon={isDollar ? dollar : percentage}
                        className="body-N w-full"
                        placeholder="Enter Amount"
                        value={item.amount_broker ?? ""}
                        onChange={(e) =>
                          updateBroker(index, "amount_broker", e.target.value)
                        }
                      />

                      <button
                        type="button"
                        onClick={() =>
                          updateBroker(
                            index,
                            "type",
                            isDollar ? "percentage" : "dollar"
                          )
                        }
                        className="px-3 py-1 text-sm border rounded-lg bg-gray-100 hover:bg-gray-200"
                      >
                        {isDollar ? "%" : "$"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>


        {/* PAY TO BDS */}
        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="head-4">Pay to BDS</h3>
            <button
              type="button"
              onClick={addBDS}
              className="text-sm text-blue-600 font-semibold hover:underline"
            >
              + Add BDS
            </button>
          </div>

          {(formData.bds?.length ? formData.bds : [{}]).map((item, index) => {
            const isDollar = item.type === "dollar"

            return (
              <div key={index} className="mb-4 p-3 rounded-lg shadow-sm bg-white border border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">BDS Name</label>
                  {(formData.bds?.length || 0) > 1 && (
                    <button
                      type="button"
                      onClick={() => removeBDS(index)}
                      className="text-xs text-red-600 hover:underline font-semibold"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="mb-3">
                  <SearchDropdown
                    placeholder="Search BDS..."
                    text={item?.contact?.name || ""}
                    options={bdsOptions[index] || []}
                    fetchSuggestions={(query) => fetchBdsSuggestions(query, index)}
                    onSetOptions={(opts) => setBdsOptions((prev) => ({ ...prev, [index]: opts }))}
                    handleKeyDown={(e) => {
                      if (e.key === "Backspace") {
                        updateBDS(index, "contact", { id: "", name: "" })
                      }
                    }}
                    handleSelect={(option) => {
                      updateBDS(index, "contact", { id: option.value, name: option.label })
                      setBdsOptions((prev) => ({ ...prev, [index]: [] }))
                    }}
                  />
                </div>

                <div className="flex gap-4 items-center">
                  {/* Contribution % */}
                  <div className="flex-1">
                    <label className="block mb-1 dark-H head-4">
                      Contribution %
                    </label>

                    <InputWithIcon
                      icon={percentage}
                      placeholder="Contribution %"
                      type="number"
                      className="input-field body-N"
                      value={item.commission_amount_bds ?? ""}
                      onChange={(e) =>
                        updateBDS(
                          index,
                          "commission_amount_bds",
                          e.target.value
                        )
                      }
                    />
                  </div>

                  {/* Commission */}
                  <div className="flex-1">
                    <label className="block mb-1 dark-H head-4">
                      Commission
                    </label>

                    <div className="flex items-center gap-2">
                      <InputWithIcon
                        icon={isDollar ? dollar : percentage}
                        className="body-N w-full"
                        placeholder="Enter Amount"
                        value={item.amount ?? ""}
                        onChange={(e) =>
                          updateBDS(index, "amount", e.target.value)
                        }
                      />

                      <button
                        type="button"
                        onClick={() =>
                          updateBDS(
                            index,
                            "type",
                            isDollar ? "percentage" : "dollar"
                          )
                        }
                        className="px-3 py-1 text-sm border rounded-lg bg-gray-100 hover:bg-gray-200"
                      >
                        {isDollar ? "%" : "$"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>


        {/* COMPANY GROSS COMMISSION */}
        <div className="mt-6">
          <label className="dark-H head-4">Company Gross Commission</label>
          <InputWithIcon
            icon={dollar}
            type="number"
            className="input-field body-N"
            placeholder="Company Gross Commission"
            value={formData.company_gross_commission ?? ""}
            onChange={(e) => handleNumericFieldChange("company_gross_commission", e, "company_gross_commission_manual")}
          />
        </div>

        {/* ERROR ABOVE SAVE */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">
              {typeof error === "string"
                ? error
                : JSON.stringify(error)}
            </p>
          </div>
        )}

        <div className="mt-6">
          <button
            type="submit"
            className="save-button light-L head-5 green-bg-H"
            disabled={isSubmitting || disable}
          >
            Save
          </button>

          <button
            type="button"
            onClick={() => {
              onSetError(null)
              handleClose()
            }}
            className="green-H ml-5"
          >
            Cancel
          </button>

        </div>
      </form>

      {/* CONFIRM MODAL */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <div className="flex justify-between pr-5 mb-4">
              <h3 className="head-2 dark-H">Confirmation</h3>
              <img src={Close} alt="close" role="button" onClick={handleConfirmNo} />
            </div>

            <p className="dark-M body-N mb-4">
              {(() => {
                const combined = calculateBrokerShare() + calculateBdsShare()
                const company = toNumber(formData.company_gross_commission)
                const comparison = combined > company ? 'greater than' : 'less than'
                return `Combined pay to Brokers and BDS is ${comparison} the Company Gross Commission. Do you still want to move forward?`
              })()}
            </p>
            <div className="flex gap-3">
              <button onClick={handleConfirmNo} className="green-H">
                No
              </button>
              <button
                onClick={handleConfirmYes}
                className="save-button light-L head-5 green-bg-H"
                disabled={isSubmitting || disable}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AddCommissionData